import React, { useEffect, useState, useRef } from "react";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useTranslation } from "react-i18next";
import { Toast } from "primereact/toast";

export default function ContactMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const toast = useRef(null);
  const { t } = useTranslation();

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await backendVerifiedUser.get("/contact-submissions");
      // backend returns { data: result, total, page, limit }
      setMessages(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: t("Error"), detail: t("Failed to load messages"), life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const confirmDelete = (msg) => {
    confirmDialog({
      message: t("Are you sure you want to delete this message?"),
      header: t("Confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: t("Delete"),
      rejectLabel: t("Cancel"),
      accept: () => handleDelete(msg.id),
    });
  };

  const handleDelete = async (id) => {
    try {
      await backendVerifiedUser.delete(`/contact-submissions/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.current?.show({ severity: "success", summary: t("Deleted"), detail: t("Message deleted"), life: 2500 });
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: t("Error"), detail: t("Failed to delete message"), life: 3000 });
    }
  };

  const onView = (msg) => {
    setSelectedMessage(msg);
    setDetailVisible(true);
  };

  const subjectBodyTemplate = (rowData) => (
    <div>
      <div className="font-medium">{rowData.subject || "-"}</div>
      <div className="text-sm text-gray-500">{rowData.email}</div>
    </div>
  );

  const actionBodyTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button icon="pi pi-eye" className="p-button-sm" onClick={() => onView(rowData)} />
      <Button icon="pi pi-trash" className="p-button-sm p-button-danger" onClick={() => confirmDelete(rowData)} />
    </div>
  );

  const header = (
    <div className="flex items-center justify-between">
      <div className="flex gap-2 items-center">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText placeholder={t("Search")} value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} />
        </span>
      </div>
      <div>
        <Button icon="pi pi-refresh" className="p-button-outlined" onClick={fetchMessages} />
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="mb-4">
        <h2 className="text-xl font-semibold">{t("Contact Submissions")}</h2>
        <p className="text-sm text-gray-600">{t("List of messages submitted via Contact Us form")}</p>
      </div>

      <div className="card">
        <DataTable
          value={messages}
          paginator
          rows={10}
          loading={loading}
          globalFilter={globalFilter}
          header={header}
          emptyMessage={t("No messages found")}
          responsiveLayout="scroll"
        >
          <Column field="id" header={t("ID")} style={{ width: "6rem" }} />
          <Column field="name" header={t("Name")} />
          <Column field="email" header={t("Email")} />
          <Column header={t("Subject / Email")} body={subjectBodyTemplate} />
          <Column field="created_at" header={t("Created At")} style={{ width: "12rem" }} />
          <Column header={t("Actions")} body={actionBodyTemplate} style={{ width: "8rem" }} />
        </DataTable>
      </div>

      <Dialog header={t("Message Details")} visible={detailVisible} style={{ width: "50vw" }} onHide={() => setDetailVisible(false)}>
        {selectedMessage ? (
          <div className="space-y-3">
            <div>
              <strong>{t("Name")}:</strong> {selectedMessage.name}
            </div>
            <div>
              <strong>{t("Email")}:</strong> {selectedMessage.email}
            </div>
            <div>
              <strong>{t("Subject")}:</strong> {selectedMessage.subject}
            </div>
            <div>
              <strong>{t("Message")}:</strong>
              <div className="mt-2 p-3 bg-gray-50 rounded border">{selectedMessage.description}</div>
            </div>
            <div>
              <strong>{t("Submitted At")}:</strong> {selectedMessage.created_at}
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
